import { useEffect, useState } from 'react'
import {
  Loader2,
  Mail,
  MessageSquare,
  Send,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  PenLine,
  Paperclip,
  FileText,
  FileSpreadsheet,
  X,
} from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Badge } from '../../lib/shadcn/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../lib/shadcn/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import {
  useSendMessage,
  useGetCommunications,
  useGetEmailTemplates,
  useUploadEmailImage,
  useGetEmailSignature,
} from '../../hooks/backend/crm'
import { toast } from '../../lib/shadcn/sonner'
import RichTextEditor from '../../components/RichTextEditor'
import EmailBody from '../../components/EmailBody'
import AiEmailAssistant from '../../components/AiEmailAssistant'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../lib/shadcn/dropdown-menu'
import { PriceSheetDiscountItems, usePriceSheetFiles, type PriceSheetFile, type PriceSheetKind } from './PriceSheetMenu'
import { blobToBase64 } from '../../utils/priceSheet'

export interface MessageTarget {
  leadId?: number
  customerId?: string
  name: string
  email: string
  phone: string
  /** Email subject and body to start from (e.g. an AI reorder reminder). */
  draft?: { subject: string; body: string }
}

interface Communication {
  id: number
  channel: 'email' | 'sms'
  recipient: string
  subject: string | null
  body: string
  status: string
  error: string | null
  sent_by: string | null
  sent_at: string
}

interface EmailTemplate {
  id: number
  name: string
  subject: string
  body: string
}

// Read a File into a base64 string (without the data: prefix).
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

interface Props {
  target: MessageTarget | null
  smsConfigured: boolean
  onOpenChange: (open: boolean) => void
}

function formatDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function MessageDialog({ target, smsConfigured, onOpenChange }: Props) {
  const sendMessage = useSendMessage()
  const getCommunications = useGetCommunications()
  const getEmailTemplates = useGetEmailTemplates()
  const uploadEmailImage = useUploadEmailImage()
  const getEmailSignature = useGetEmailSignature()
  const priceSheet = usePriceSheetFiles()

  const [channel, setChannel] = useState<'email' | 'sms'>('email')
  const [subject, setSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [smsBody, setSmsBody] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [sending, setSending] = useState(false)
  const [history, setHistory] = useState<Communication[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [attachments, setAttachments] = useState<PriceSheetFile[]>([])

  const loadHistory = () => {
    if (!target) return
    setLoadingHistory(true)
    const params = target.leadId !== undefined ? { lead_id: target.leadId } : { customer_id: target.customerId }
    getCommunications
      .trigger(params, { skipCache: true })
      .then((data: unknown) => setHistory((data as Communication[] | undefined) ?? []))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false))
  }

  useEffect(() => {
    if (!target) return
    setChannel('email')
    setSubject(target.draft?.subject ?? '')
    setEmailBody(target.draft?.body ?? '')
    setSmsBody('')
    setTemplateId('')
    setAttachments([])
    setHistory([])
    loadHistory()
    void getEmailTemplates.trigger({})
    void getEmailSignature.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  const insertSignature = () => {
    const sig = (getEmailSignature.data as { signature?: string } | undefined)?.signature ?? ''
    if (!sig.trim()) {
      toast.error('No signature saved yet. Add one in Settings → Email.')
      return
    }
    setEmailBody(prev => (prev && prev.trim() ? `${prev}<p><br></p>` : '') + sig)
  }

  const templates = (getEmailTemplates.data as EmailTemplate[] | undefined) ?? []

  const applyTemplate = (id: string) => {
    setTemplateId(id)
    const t = templates.find(tpl => String(tpl.id) === id)
    if (!t) return
    setSubject(t.subject ?? '')
    setEmailBody(t.body ?? '')
  }

  const handleImageUpload = async (file: File): Promise<string | null> => {
    try {
      const base64 = await fileToBase64(file)
      const res = (await uploadEmailImage.trigger({
        fileName: file.name,
        data: base64,
        mimeType: file.type,
      })) as { url?: string } | undefined
      if (!res?.url) {
        toast.error('Image upload failed')
        return null
      }
      return res.url
    } catch {
      toast.error('Image upload failed')
      return null
    }
  }

  const attachPriceSheet = async (kind: PriceSheetKind) => {
    try {
      const file = await priceSheet.build(kind)
      setAttachments(prev => [...prev.filter(a => a.kind !== kind), file])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not build the price sheet')
    }
  }

  const recipient = channel === 'email' ? target?.email ?? '' : target?.phone ?? ''

  const handleSend = async () => {
    if (!target) return
    if (!recipient.trim()) {
      toast.error(`No ${channel === 'email' ? 'email address' : 'phone number'} on file`)
      return
    }
    const isEmail = channel === 'email'
    const body = isEmail ? emailBody : smsBody
    const plainText = isEmail
      ? emailBody.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      : smsBody.trim()
    if (!plainText) {
      toast.error('Message body is required')
      return
    }
    if (isEmail && !subject.trim()) {
      toast.error('Subject is required for email')
      return
    }
    setSending(true)
    try {
      const emailAttachments =
        isEmail && attachments.length > 0
          ? await Promise.all(
              attachments.map(async a => ({
                name: a.fileName,
                content_type: a.blob.type,
                data: await blobToBase64(a.blob),
              }))
            )
          : []
      const result = (await sendMessage.trigger({
        channel,
        recipient,
        subject,
        body,
        ...(isEmail ? { content_type: 'html' as const } : {}),
        ...(emailAttachments.length > 0 ? { attachments: emailAttachments } : {}),
        ...(target.leadId !== undefined ? { lead_id: target.leadId } : {}),
        ...(target.customerId !== undefined ? { customer_id: target.customerId } : {}),
      })) as { ok: boolean; error?: string }
      if (result.ok) {
        toast.success(`${isEmail ? 'Email' : 'SMS'} sent to ${target.name}`)
        setEmailBody('')
        setSmsBody('')
        setSubject('')
        if (isEmail) setAttachments([])
      } else {
        toast.error(result.error ?? 'Failed to send message')
      }
      loadHistory()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open={target !== null}
      onOpenChange={open => {
        if (!open && !sending) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Message {target?.name}</DialogTitle>
          <DialogDescription>Send an email or SMS and review past communication.</DialogDescription>
        </DialogHeader>

        <Tabs value={channel} onValueChange={v => setChannel(v as 'email' | 'sms')}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="email">
              <Mail className="w-4 h-4 mr-1.5" /> Email
            </TabsTrigger>
            <TabsTrigger value="sms">
              <MessageSquare className="w-4 h-4 mr-1.5" /> SMS
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-3 pt-3">
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input value={target?.email ?? ''} readOnly className="bg-muted" placeholder="No email on file" />
            </div>
            {templates.length > 0 && (
              <div className="space-y-1.5">
                <Label>Template</Label>
                <Select value={templateId} onValueChange={applyTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Start from a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="msg-subject">Subject</Label>
              <Input id="msg-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>Message</Label>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 h-7"
                    onClick={insertSignature}
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    Insert signature
                  </Button>
                  <AiEmailAssistant
                    {...(target?.name ? { recipientName: target.name } : {})}
                    existingBody={emailBody}
                    onGenerated={r => {
                      if (r.subject) setSubject(r.subject)
                      setEmailBody(r.body)
                    }}
                  />
                </div>
              </div>
              <RichTextEditor
                value={emailBody}
                onChange={setEmailBody}
                onImageUpload={handleImageUpload}
                placeholder="Write your email…"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1.5 h-7" disabled={priceSheet.building !== null}>
                    {priceSheet.building ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Paperclip className="h-3.5 w-3.5" />
                    )}
                    {priceSheet.building ? 'Building price sheet…' : 'Attach price sheet'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <PriceSheetDiscountItems discount={priceSheet.discount} onChange={priceSheet.setDiscount} />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void attachPriceSheet('pdf')}>
                    <FileText className="w-4 h-4" /> PDF catalog
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void attachPriceSheet('xlsx')}>
                    <FileSpreadsheet className="w-4 h-4" /> Excel sheet
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {attachments.map(a => (
                <Badge key={a.kind} variant="secondary" className="gap-1 font-normal">
                  {a.kind === 'pdf' ? <FileText className="w-3 h-3" /> : <FileSpreadsheet className="w-3 h-3" />}
                  {a.fileName}
                  <span className="text-muted-foreground">({Math.max(1, Math.round(a.blob.size / 1024))} KB)</span>
                  <button
                    type="button"
                    className="ml-0.5 rounded hover:text-destructive"
                    onClick={() => setAttachments(prev => prev.filter(p => p.kind !== a.kind))}
                    aria-label={`Remove ${a.fileName}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="sms" className="space-y-3 pt-3">
            {!smsConfigured && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>SMS isn&apos;t configured yet. Add a Twilio Account SID and From number in CRM Settings.</span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input value={target?.phone ?? ''} readOnly className="bg-muted" placeholder="No phone on file" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="msg-sms-body">Message</Label>
              <Textarea
                id="msg-sms-body"
                value={smsBody}
                onChange={e => setSmsBody(e.target.value)}
                rows={5}
                placeholder="Write your text message..."
              />
            </div>
          </TabsContent>

          <Button className="w-full mt-3" onClick={handleSend} disabled={sending || priceSheet.building !== null}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : `Send ${channel === 'email' ? 'Email' : 'SMS'}`}
          </Button>
        </Tabs>

        <div className="border-t pt-4 mt-1">
          <h3 className="text-sm font-medium text-foreground mb-2">History</h3>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No messages yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map(item => (
                <div key={item.id} className="rounded-md border border-border p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="gap-1">
                      {item.channel === 'email' ? <Mail className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                      {item.channel.toUpperCase()}
                    </Badge>
                    {item.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-3 h-3" /> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-destructive">
                        <XCircle className="w-3 h-3" /> Failed
                      </span>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground">{formatDate(item.sent_at)}</span>
                  </div>
                  {item.subject && <p className="font-medium text-foreground">{item.subject}</p>}
                  <EmailBody body={item.body} className="text-muted-foreground text-sm" />
                  {item.error && <p className="text-xs text-destructive mt-1">{item.error}</p>}
                  {item.sent_by && <p className="text-xs text-muted-foreground mt-1">by {item.sent_by}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
